import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const SeriesManagementModal = ({
  isOpen,
  onClose,
  invoiceSeries,
  seriesFormData,
  setSeriesFormData,
  editingSeries,
  setEditingSeries,
  onCreateSeries,
  onUpdateSeries,
  onDeleteSeries,
  onToggleSeriesActive,
  formatDate
}) => {
  const { t } = useLanguage();

  if (!isOpen) return null;

  const handleClose = () => {
    onClose();
    setEditingSeries(null);
    setSeriesFormData({ name: '', prefix: '', startingNumber: 1, isActive: true });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">{t('manageInvoiceSeries')}</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 p-2"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Create/Edit Series Form */}
          <div className="mb-8 bg-gray-50 p-6 rounded-lg">
            <h4 className="text-lg font-medium text-gray-900 mb-4">
              {editingSeries ? t('editSeries') : t('createNewSeries')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('seriesPrefix')}</label>
                <input
                  type="text"
                  value={seriesFormData.prefix}
                  onChange={(e) => setSeriesFormData(prev => ({ ...prev, prefix: e.target.value.toUpperCase() }))}
                  placeholder={t('enterSeriesPrefix')}
                  className="w-full border-gray-300 border rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('startingNumber')}</label>
                <input
                  type="number"
                  value={seriesFormData.startingNumber}
                  onChange={(e) => setSeriesFormData(prev => ({ ...prev, startingNumber: parseInt(e.target.value) || 1 }))}
                  placeholder={t('enterStartingNumber')}
                  min="1"
                  className="w-full border-gray-300 border rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm p-2"
                />
              </div>
            </div>
            <div className="flex justify-end">
              {editingSeries ? (
                <div className="space-x-2">
                  <button
                    onClick={() => {
                      setEditingSeries(null);
                      setSeriesFormData({ name: '', prefix: '', startingNumber: 1, isActive: true });
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-400"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={onUpdateSeries}
                    className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700"
                  >
                    {t('save')}
                  </button>
                </div>
              ) : (
                <button
                  onClick={onCreateSeries}
                  className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700"
                >
                  {t('createSeries')}
                </button>
              )}
            </div>
          </div>

          {/* Series List */}
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-4">{t('invoiceSeries')}</h4>
            {invoiceSeries.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">{t('noSeries')}</p>
                <p className="text-sm text-gray-400">{t('createFirstSeries')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('seriesPrefix')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('currentNumber')}
                      </th>
                      <th className="relative px-6 py-3">
                        <span className="sr-only">{t('actions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoiceSeries.map((series) => (
                      <tr key={series.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 font-mono">{series.prefix}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{series.currentNumber}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                          <button
                            onClick={() => {
                              setEditingSeries(series);
                              setSeriesFormData({
                                name: series.name,
                                prefix: series.prefix,
                                startingNumber: series.startingNumber,
                                isActive: series.isActive
                              });
                            }}
                            className="text-purple-600 hover:text-purple-900"
                          >
                            {t('edit')}
                          </button>
                          {/* <button
                            onClick={() => onDeleteSeries(series.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            {t('delete')}
                          </button> */}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeriesManagementModal;
